import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Users, CheckCircle, ArrowRight, Shield, Clock, 
  MapPin, Phone, Mail, User, Loader2, Sparkles, ThumbsUp,
  AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Work category labels for display
const CATEGORY_LABELS = {
  peinture: "Peinture",
  plomberie: "Plomberie",
  electricite: "Électricité",
  chauffage_clim: "Chauffage / Climatisation",
  menuiserie: "Menuiserie",
  carrelage: "Carrelage",
  maconnerie: "Maçonnerie",
  toiture: "Toiture",
  isolation: "Isolation",
  renovation_globale: "Rénovation globale",
  salle_de_bain: "Salle de bain",
  cuisine: "Cuisine",
  facade: "Façade",
  jardin_exterieur: "Jardin / Extérieur",
  domotique: "Domotique",
  autre: "Autre"
};

// ==================
// LEAD INTEREST POPUP - Shown after analysis
// ==================
export function LeadInterestPopup({
  isOpen,
  onClose,
  analysisType,
  analysisId,
  projectSummary,
  analysisContent,
  detectedCategory = null,
  onLeadSubmitted
}) {
  const { user, getAuthHeaders, isPremium } = useAuth();
  const [step, setStep] = useState('interest'); // interest, form, success

  // Form state
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    city: '',
    postal_code: '',
    ville_projet: '',  // Ville du projet (OBLIGATOIRE)
    urgency: ''
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  // Reset when popup opens
  useEffect(() => {
    if (isOpen) {
      setStep('interest');
      setError('');
      setResult(null);
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        phone: '',
        city: '',
        postal_code: '',
        ville_projet: '',
        urgency: ''
      });
      setConsent(false);
    }
  }, [isOpen, user]);

  // Premium users don't see lead popups
  if (isPremium) return null;

  const handleInterestYes = () => {
    setStep('form');
  };

  const handleInterestNo = () => {
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation ville_projet OBLIGATOIRE
    if (!formData.ville_projet || formData.ville_projet.trim().length < 2) {
      setError('Veuillez renseigner la ville de votre projet');
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/leads/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          ...formData,
          analysis_type: analysisType,
          analysis_id: analysisId,
          project_summary: projectSummary,
          analysis_content: analysisContent,
          consent_artisan_contact: consent
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'envoi');
      }

      setResult(data);
      setStep('success');
      
      if (onLeadSubmitted) {
        onLeadSubmitted(data);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && step === 'interest' && onClose()}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
        >
          {/* ========== STEP 1: Interest Question ========== */}
          {step === 'interest' && (
            <>
              <div className="p-6 text-center">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-emerald-400" />
                </div>
                
                <h2 className="text-xl font-bold text-white mb-2">
                  Souhaitez-vous recevoir des devis d'artisans qualifiés ?
                </h2>
                
                <p className="text-zinc-400 text-sm mb-6">
                  Recevez gratuitement jusqu'à 3 devis de professionnels vérifiés pour votre projet.
                </p>

                {detectedCategory && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm mb-6">
                    <Sparkles className="w-4 h-4" />
                    {CATEGORY_LABELS[detectedCategory] || detectedCategory}
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  <Button
                    onClick={handleInterestYes}
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-4 text-base"
                    data-testid="lead-interest-yes"
                  >
                    <ThumbsUp className="w-5 h-5 mr-2" />
                    Oui, je veux être contacté
                  </Button>
                  
                  <Button
                    onClick={handleInterestNo}
                    variant="ghost"
                    className="w-full text-zinc-400 hover:text-white"
                    data-testid="lead-interest-no"
                  >
                    Non merci
                  </Button>
                </div>

                {/* Trust indicators */}
                <div className="mt-6 pt-4 border-t border-zinc-800 flex justify-center gap-6 text-xs text-zinc-500">
                  <span className="flex items-center gap-1">
                    <Shield className="w-3 h-3 text-emerald-400" />
                    Artisans vérifiés
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3 text-emerald-400" />
                    Réponse 48h
                  </span>
                </div>
              </div>
            </>
          )}

          {/* ========== STEP 2: Contact Form ========== */}
          {step === 'form' && (
            <>
              <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">Vos coordonnées</h3>
                <button 
                  onClick={onClose}
                  className="p-2 text-zinc-500 hover:text-white rounded-lg hover:bg-zinc-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Prénom *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Votre prénom"
                      className="pl-10 bg-zinc-800 border-zinc-700"
                      required
                      data-testid="lead-name-input"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="votre@email.com"
                      className="pl-10 bg-zinc-800 border-zinc-700"
                      required
                      data-testid="lead-email-input"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Téléphone *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="06 12 34 56 78"
                      className="pl-10 bg-zinc-800 border-zinc-700"
                      required
                      data-testid="lead-phone-input"
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Ville</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        placeholder="Paris"
                        className="pl-10 bg-zinc-800 border-zinc-700"
                        data-testid="lead-city-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Code postal</label>
                    <Input
                      type="text"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                      placeholder="75001"
                      className="bg-zinc-800 border-zinc-700"
                      maxLength={5}
                      data-testid="lead-postal-input"
                    />
                  </div>
                </div>

                {/* Urgency */}
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Quand souhaitez-vous démarrer ?</label>
                  <select
                    value={formData.urgency}
                    onChange={(e) => setFormData({...formData, urgency: e.target.value})}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                    data-testid="lead-urgency-select"
                  >
                    <option value="">Sélectionnez...</option>
                    <option value="immediate">Dès que possible</option>
                    <option value="1_month">Dans le mois</option>
                    <option value="3_months">Dans les 3 mois</option>
                    <option value="exploring">Je me renseigne</option>
                  </select>
                </div>

                {/* Ville du projet - OBLIGATOIRE */}
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">
                    Ville du projet <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                    <Input
                      type="text"
                      value={formData.ville_projet}
                      onChange={(e) => setFormData({...formData, ville_projet: e.target.value})}
                      placeholder="Ex : Nancy, Lyon, Paris..."
                      className="pl-10 bg-zinc-800 border-zinc-700 focus:border-amber-500"
                      required
                      data-testid="lead-ville-projet-input"
                    />
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">Où se situe votre projet de travaux ?</p>
                </div>

                {/* Consent */}
                <div className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg">
                  <input
                    type="checkbox"
                    id="consent"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-emerald-500"
                    required
                    data-testid="lead-consent-checkbox"
                  />
                  <label htmlFor="consent" className="text-xs text-zinc-400">
                    J'accepte d'être contacté par des artisans partenaires pour recevoir des devis. 
                    Mes données ne seront utilisées que dans ce cadre.
                  </label>
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || !consent}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-3"
                  data-testid="lead-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      Recevoir mes devis gratuits
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </>
          )}

          {/* ========== STEP 3: Success ========== */}
          {step === 'success' && (
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              
              <h2 className="text-xl font-bold text-white mb-2">
                Demande envoyée !
              </h2>
              
              <p className="text-zinc-400 text-sm mb-4">
                Des artisans qualifiés vont vous contacter dans les 48h pour vous proposer leurs devis.
              </p>

              {result?.qualification && (
                <div className="p-4 bg-zinc-800/50 rounded-xl mb-4 text-left">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Votre projet</p>
                  <p className="text-white font-medium">
                    {CATEGORY_LABELS[result.qualification.category] || result.qualification.category}
                  </p>
                  {result.qualification.budget_estimate && (
                    <p className="text-amber-400 text-sm mt-1">
                      Budget estimé : {result.qualification.budget_estimate}
                    </p>
                  )}
                </div>
              )}

              <Button
                onClick={onClose}
                className="w-full bg-zinc-800 hover:bg-zinc-700 text-white"
              >
                Fermer
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default LeadInterestPopup;
